import { IconCheck } from '@tabler/icons-react';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  Anchor,
  Box,
  Flex,
  Group,
  Icon,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';
import { useMobileDetector } from '@zorro/web/ui-utils';

import { PaymentStatusData } from './EmployeePaymentStatusVariants';

type MarkAsDoneButtonProps = {
  isDashboard?: boolean;
  onMarkAsDone?: () => void;
};

type PaymentStatusCardProps = MarkAsDoneButtonProps &
  PaymentStatusData & {
    handleLearnMoreClick?: () => Promise<void>;
    shouldMakeCardOutline?: boolean;
  };

const MarkAsDoneButton = ({
  isDashboard,
  onMarkAsDone,
}: MarkAsDoneButtonProps) => (
  <Button
    variant="primary"
    leftSection={
      <Icon icon={IconCheck} size="1.25rem" color={brand.zorroWhite} />
    }
    onClick={onMarkAsDone}
    mt={isDashboard ? 'xs' : 'sm'}
    w="fit-content"
    size={isDashboard ? 'sm' : 'md'}
  >
    <Text size={isDashboard ? 'sm' : 'md'} c={brand.zorroWhite}>
      Mark as set up
    </Text>
  </Button>
);

export const PaymentStatusCard = ({
  icon,
  toolTipLabel,
  label,
  description,
  learnMoreLink,
  onMarkAsDone,
  shouldShowMarkAsDone,
  isDashboard,
  handleLearnMoreClick,
  shouldMakeCardOutline,
}: PaymentStatusCardProps) => {
  const { isMobile } = useMobileDetector();

  const getCardStyles = () => {
    if (shouldMakeCardOutline) {
      return {
        bg: 'transparent',
        variant: 'white-border' as const,
      };
    }

    return {
      bg: isDashboard ? brand.zorroPlum200 : brand.zorroWhite,
      variant: isDashboard ? ('white' as const) : ('white-border' as const),
    };
  };

  const cardStyles = getCardStyles();

  return (
    <Card p={isDashboard ? 'md' : 'xl'} w="100%" mih="100%" {...cardStyles}>
      <Stack gap={isDashboard ? 'xs' : 'md'} h="100%">
        <Stack gap="xs">
          <Group justify="space-between">
            {icon}
            {toolTipLabel && (
              <Tooltip label={toolTipLabel} position="top">
                <Box
                  bg={isDashboard ? brand.zorroPlum300 : brand.zorroPlum100}
                  px="xs"
                  style={{ borderRadius: 4 }}
                >
                  <Text size="sm">Action needed</Text>
                </Box>
              </Tooltip>
            )}
          </Group>
          <Text size="sm" fw={600}>
            {label}
          </Text>
          {description && (
            <Text
              size="sm"
              fw={400}
              c="var(--color-icon-fill-system-secondary)"
              maw={isDashboard ? '85%' : '100%'}
              style={{ whiteSpace: 'pre-line' }}
            >
              {description}
            </Text>
          )}
        </Stack>

        <Flex
          direction={isMobile ? 'row-reverse' : 'column'}
          justify={isMobile ? 'space-between' : 'initial'}
          align={isMobile ? 'center' : 'baseline'}
        >
          {learnMoreLink && (
            <Anchor
              mt={isMobile ? 'xs' : '0rem'}
              href={learnMoreLink}
              target="_blank"
              size={isDashboard ? 'sm' : 'md'}
              shouldShowExternalIcon={!isMobile}
              onClick={handleLearnMoreClick}
            >
              Learn more
            </Anchor>
          )}

          {shouldShowMarkAsDone && onMarkAsDone && (
            <MarkAsDoneButton
              isDashboard={isDashboard}
              onMarkAsDone={onMarkAsDone}
            />
          )}
        </Flex>
      </Stack>
    </Card>
  );
};
